import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import hero from "@/assets/bluebonnet/wedding-tent-hero.jpg";
import heroPicture from "@/assets/bluebonnet/wedding-tent-hero.jpg?w=640;1024;1600&format=avif;webp;jpeg&as=picture";
import letsTalkTent from "@/assets/bluebonnet/lets-talk-event-tent.jpg";
import printedTent from "@/assets/bluebonnet/printed-logo-vendor-tent.jpg";
import festivalRow from "@/assets/bluebonnet/festival-tent-row-sidewalls.jpg";
import tentLighting from "@/assets/bluebonnet/tent-string-lighting.jpg";

const FAQS = [
  {
    q: "What sizes of commercial event tents do you offer?",
    a: "Everything from 10x10 and 10x20 pop-up canopies for vendor booths and farmers markets up to larger frame and peak tents for weddings, festivals and corporate events. Tell us your booth footprint or guest count and we'll size it.",
  },
  {
    q: "Can you print our logo on the tent top?",
    a: "Yes. We do custom-printed tent tops, valances, sidewalls, table covers, banners and feather flags so your booth reads as your brand from across a market or festival.",
  },
  {
    q: "How is a commercial tent different from a big-box canopy?",
    a: "Commercial frames use heavier aluminum or steel, real hardware and replaceable tops. When a top wears out or a leg bends you repair or replace that part instead of throwing the whole tent away — and we sew and service them here in Seguin.",
  },
  {
    q: "Do you sell sidewalls and accessories separately?",
    a: "Yes — solid and window sidewalls, half walls, zippered doors, weight bags, stakes, ratchet straps, replacement tops and hardware, plus table covers and banners to match.",
  },
  {
    q: "Can you repair a tent we already own?",
    a: "Yes. We re-sew blown seams, patch tops, replace zippers and windows, rebuild sidewalls and fabricate replacement tops for existing frames — including tents we didn't sell.",
  },
  {
    q: "Where do you deliver and set up event tents?",
    a: "San Antonio, Austin, New Braunfels, Seguin, Boerne and across Central and South Texas. Setup help is available for an additional fee — just ask when you request your quote.",
  },
];

export const Route = createFileRoute("/commercial-event-tents")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Commercial Event Tents & Custom Canopies Texas | Texas Made Shade",
      description:
        "Commercial event tents, pop-up canopies, custom-printed tent tops, sidewalls and vendor / farmers-market tents — Bluebonnet Series™, sewn and serviced in Seguin for San Antonio and Austin.",
      image: hero,
      socialTitle: "Commercial Event Tents & Custom Canopies — Texas Made Shade",
      socialDescription:
        "Pop-up canopies, printed tent tops, sidewalls and vendor booth tents for markets, festivals and events across Texas.",
      keywords:
        "commercial event tents texas, custom canopy tents san antonio, printed tent tops, pop up tents austin, vendor booth tents, farmers market tents texas, tent sidewalls, festival tents texas, custom printed canopy, event tent repair texas",
    }),
    scripts: serviceHeadScripts({
      path: "/commercial-event-tents",
      serviceName: "Commercial Event Tents & Custom Canopies",
      serviceType: "Commercial event tent sales, custom printed canopies, sidewalls and tent repair",
      breadcrumbName: "Commercial Event Tents",
      description:
        "Commercial event tents and custom canopies from Texas Made Shade — pop-up tents, printed tops, sidewalls and vendor / farmers-market booths, serving San Antonio, Austin and Central Texas.",
      faqs: FAQS,
    }),
  }),
  component: CommercialEventTentsPage,
});

function CommercialEventTentsPage() {
  return (
    <ServicePage
      currentPath="/commercial-event-tents"
      serviceName="Commercial Event Tents & Custom Canopies"
      breadcrumbs={[
        { label: "Tents, Vendor Booths & Outdoor Markets", to: "/bluebonnet-series" },
        { label: "Commercial Event Tents" },
      ]}
      keywordOutline={[
        { id: "overview", label: "Commercial Event Tents & Custom Canopies" },
        { id: "why-us", label: "Why Buy Your Tent From Us" },
        { id: "commercial-event-tents", label: "Commercial Event Tents" },
        { id: "pop-up-tents", label: "Pop-Up Tents" },
        { id: "custom-printed-tent-tops", label: "Custom-Printed Tent Tops" },
        { id: "tent-sidewalls", label: "Tent Sidewalls" },
        { id: "vendor-and-farmers-market-tents", label: "Vendor & Farmers-Market Tents" },
        { id: "faqs", label: "Event Tent FAQs" },
      ]}
      eyebrow="★ BLUEBONNET SERIES™ · TENTS & EVENT CANOPIES ★"
      title="Commercial Event Tents & Custom Canopies in Texas"
      intro="Commercial-grade event tents, pop-up canopies, printed tops, sidewalls and vendor booth setups — supplied, customized and repaired in our Seguin shop for markets, festivals, weddings and corporate events across San Antonio, Austin and Central Texas."
      heroImage={heroPicture}
      features={[
        "Commercial frame & peak tents for events",
        "10x10 and 10x20 pop-up canopies",
        "Custom-printed tops, valances & sidewalls",
        "Solid, window and half sidewalls in stock sizes",
        "Vendor booth, farmers market & festival packages",
        "Repairs, replacement tops and hardware",
      ]}
      body={[
        {
          heading: "Commercial Event Tents",
          text: "Frame and peak tents built for real event use — weddings, receptions, corporate gatherings, ceremonies and church events. Heavier frames, replaceable tops and hardware you can actually service, so the tent lasts season after season instead of one windy Saturday.",
          bullets: [
            "Frame and peak tents sized to guest count",
            "Replaceable tops instead of throwaway canopies",
            "Weights, stakes and tie-down packages",
            "Setup help available for an additional fee",
          ],
          cta: { label: "Get an event tent quote", href: "/estimate-quotes#general" },
        },
        {
          heading: "Pop-Up Tents",
          text: "The workhorse of any booth: 10x10 and 10x20 commercial pop-ups that go up in minutes and travel every weekend. We stock commercial-grade frames — not big-box canopies — and customize the tops, walls and accessories in-house.",
          bullets: [
            "10x10 and 10x20 commercial pop-up frames",
            "Straight-leg and heavy-duty options",
            "Roller bags, weight bags and stakes",
            "Replacement tops for frames you already own",
          ],
          image: {
            src: printedTent,
            alt: "Commercial pop-up canopy tent with a printed logo top set up as a vendor booth at a Texas outdoor market",
            caption: "Commercial pop-up canopies customized with printed tops in our Seguin shop.",
          },
        },
        {
          heading: "Custom-Printed Tent Tops",
          text: "Your booth should be readable from across the market. We print full-color tops, peak panels and valances with your logo, colors and lettering so customers spot you before they reach you — matching table covers, banners and feather flags available too.",
          bullets: [
            "Full-color printed tops and valances",
            "Logos, lettering and brand colors matched",
            "Matching table covers, banners and flags",
            "Reprints and new tops for existing frames",
          ],
          cta: { label: "Send us your logo", href: "/estimate-quotes#general" },
        },
        {
          heading: "Tent Sidewalls",
          text: "Sidewalls turn a canopy into a booth — sun blocked on the west side, wind cut down, product protected overnight. We supply solid, window and half walls, sew zippered doors and custom sizes, and repair the walls you already have.",
          bullets: [
            "Solid, window and half sidewalls",
            "Zippered doors and custom widths",
            "Printed sidewalls for branding",
            "Repairs, new zippers and re-hemming",
          ],
          image: {
            src: festivalRow,
            alt: "Row of white festival tents with sidewalls installed along a Texas event midway",
            caption: "Sidewalls for shade, wind and overnight security at festivals and markets.",
          },
        },
        {
          heading: "Vendor and Farmers-Market Tents",
          text: "Market vendors, food trailers, boutiques and festival exhibitors need a setup that loads fast and looks sharp every weekend. We put together complete booth packages — tent, printed top, walls, table covers and banners — and service them when the road wears them out.",
          bullets: [
            "Complete vendor booth packages",
            "Farmers market and festival canopies",
            "Boutique and mobile-retail setups",
            "Table covers, banners and feather flags",
          ],
          image: {
            src: tentLighting,
            alt: "Event tent lit with warm string lighting at a Texas market in the evening",
            caption: "String lighting and finishing touches for evening markets and festivals.",
          },
        },
        {
          heading: "Part of the Bluebonnet Series™",
          text: "Commercial event tents live in our Bluebonnet Series™ — Tents, Vendor Booths & Outdoor Markets. Looking for luxury canvas lodging instead? Safari and glamping tents belong to the Rio Grande Series™ hospitality line.",
          bullets: [
            "Bluebonnet Series™ — events, vendors, markets, festivals",
            "Rio Grande Series™ — hotels, resorts and glamping properties",
            "Tent repair, recover and replacement parts",
            "One shop for tents, walls, covers and banners",
          ],
          cta: { label: "Explore the Bluebonnet Series™", href: "/bluebonnet-series" },
        },
      ]}
      applicationsEyebrow="APPLICATIONS"
      applicationsHeading="Where Our Event Tents & Canopies Go To Work"
      applications={[
        "Farmers markets & craft markets",
        "Festivals & city events",
        "Vendor booths & mobile boutiques",
        "Weddings & receptions",
        "Corporate & sponsor activations",
        "Church and school events",
        "Food trailers & concessions",
        "Trade shows & expos",
        "Sports tournaments & team tents",
        "Grand openings & sidewalk sales",
      ]}
      letsTalkImageSrc={letsTalkTent}
      letsTalkImageAlt="Commercial event tent set up on a Texas lawn at golden hour — event tents, printed tops and sidewalls by Texas Made Shade"
      faqs={FAQS}
    />
  );
}
